import { useState } from 'react'
import { messagesApi } from '../../api/messages'
import { useAuthStore } from '../../stores/authStore'
import { useUiStore } from '../../stores/uiStore'
import type { Reaction } from '../../types'

export const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '🎉']

interface Props {
  chatId: string
  messageId: string
  reactions: Reaction[]
  isMine: boolean
}

export function MessageReactions({ chatId, messageId, reactions, isMine }: Props) {
  const { userId } = useAuthStore()
  const { addToast } = useUiStore()

  if (reactions.length === 0) return null

  const myReactions = new Set(
    reactions.flatMap((r) => (r.user_ids.includes(userId ?? '') ? [r.emoji] : []))
  )

  const handleReact = async (emoji: string) => {
    try {
      if (myReactions.has(emoji)) {
        await messagesApi.unreact(chatId, messageId, emoji)
      } else {
        await messagesApi.react(chatId, messageId, emoji)
      }
    } catch {
      addToast('error', 'Не удалось поставить реакцию')
    }
  }

  return (
    <div className={`flex flex-wrap items-center gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
      {reactions.map((r) => {
        const mine = myReactions.has(r.emoji)
        const names = r.user_ids.slice(0, 5).join(', ') + (r.user_ids.length > 5 ? '…' : '')
        return (
          <button
            key={r.emoji}
            onClick={() => handleReact(r.emoji)}
            title={names}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm transition-all active:scale-90
              ${mine
                ? 'bg-accent/20 text-accent border border-accent/40 hover:bg-accent/30'
                : 'bg-bg-input text-txt-primary border border-divider hover:bg-bg-hover'
              }`}
          >
            <span className="leading-none">{r.emoji}</span>
            <span className="text-xs font-semibold tabular-nums">{r.count}</span>
          </button>
        )
      })}
    </div>
  )
}

interface AddButtonProps {
  chatId: string
  messageId: string
  reactions: Reaction[]
  isMine: boolean
  visible: boolean
}

/**
 * Floating reaction picker button — rendered OUTSIDE the message bubble
 * so it doesn't affect bubble dimensions.
 */
export function AddReactionButton({ chatId, messageId, reactions, isMine, visible }: AddButtonProps) {
  const { userId } = useAuthStore()
  const { addToast } = useUiStore()
  // Use a local state approach via a wrapping div that shows/hides the picker
  const myReactions = new Set(
    reactions.flatMap((r) => (r.user_ids.includes(userId ?? '') ? [r.emoji] : []))
  )

  const handleReact = async (emoji: string) => {
    try {
      if (myReactions.has(emoji)) {
        await messagesApi.unreact(chatId, messageId, emoji)
      } else {
        await messagesApi.react(chatId, messageId, emoji)
      }
    } catch {
      addToast('error', 'Не удалось поставить реакцию')
    }
  }

  if (!visible) return null

  return (
    <AddButtonInner
      isMine={isMine}
      myReactions={myReactions}
      onReact={handleReact}
    />
  )
}

function AddButtonInner({
  isMine,
  myReactions,
  onReact,
}: {
  isMine: boolean
  myReactions: Set<string>
  onReact: (emoji: string) => void
}) {
  // Local state just for the picker open/close — kept in this inner component
  // to avoid re-rendering the parent MessageItem on every open/close.
  // We use a "trick": render the button with group hover via CSS only,
  // and use a checkbox-like pattern. Actually, we just use useState here.
  // This component is small and unmounts when not hovered anyway.
  const [open, setOpen] = useState(false)

  return (
    <div
      className={`absolute -bottom-3 z-20 ${isMine ? 'right-2' : 'left-2'}`}
      // Stop the click from propagating to MessageItem's row click handler
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className={`absolute bottom-8 z-40 bg-bg-primary border border-divider rounded-2xl
                        shadow-xl px-2 py-1.5 flex gap-0.5 animate-fade-in
                        ${isMine ? 'right-0' : 'left-0'}`}
          >
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => { onReact(emoji); setOpen(false) }}
                className={`text-xl w-9 h-9 flex items-center justify-center rounded-xl
                            hover:bg-bg-hover transition-colors active:scale-90
                            ${myReactions.has(emoji) ? 'bg-accent/15 ring-1 ring-accent/40' : ''}`}
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
        </>
      )}
      <button
        onClick={() => setOpen((s) => !s)}
        className={`flex items-center justify-center w-6 h-6 rounded-full border transition-colors text-sm leading-none shadow-sm
          ${open
            ? 'bg-accent/20 border-accent/40 text-accent'
            : 'bg-bg-primary border-divider text-txt-meta hover:text-txt-primary hover:bg-bg-hover'
          }`}
        title="Добавить реакцию"
      >
        🙂
      </button>
    </div>
  )
}
